import { useEffect, useState } from 'react';
import { ShieldCheck, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DataTrustFooterProps {
  variantId: string;
  modelId?: string | null;
  updatedAt?: string | null;
}

type SourceEntry = {
  label: string;
  url: string | null;
  lastVerifiedAt: string | null;
};

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function DataTrustFooter({ variantId, modelId, updatedAt }: DataTrustFooterProps) {
  const [sources, setSources] = useState<SourceEntry[]>([]);
  const [latestVerified, setLatestVerified] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ids = [variantId, modelId].filter(Boolean) as string[];

    (async () => {
      try {
        const { data, error } = await supabase
          .from('data_points')
          .select('source_label, source_url, last_verified_at')
          .in('entity_id', ids)
          .limit(200);

        if (error || cancelled || !data) return;

        const map = new Map<string, SourceEntry>();
        let latest: string | null = null;

        for (const row of data) {
          const label = row.source_label?.trim();
          if (label) {
            const existing = map.get(label);
            if (
              !existing ||
              (row.last_verified_at && (!existing.lastVerifiedAt || row.last_verified_at > existing.lastVerifiedAt))
            ) {
              map.set(label, {
                label,
                url: row.source_url ?? existing?.url ?? null,
                lastVerifiedAt: row.last_verified_at ?? existing?.lastVerifiedAt ?? null,
              });
            }
          }
          if (row.last_verified_at && (!latest || row.last_verified_at > latest)) {
            latest = row.last_verified_at;
          }
        }

        setSources(Array.from(map.values()).slice(0, 6));
        setLatestVerified(latest);
      } catch {
        // Stille fejl — sektionen falder tilbage til standardtekst
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [variantId, modelId]);

  const verifiedLabel = formatDate(latestVerified) ?? formatDate(updatedAt ?? null);

  return (
    <section className="mt-8 rounded-xl border border-border bg-muted/30 p-4 md:p-5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-[18px] h-[18px] text-primary" />
        </div>
        <div className="min-w-0 space-y-2">
          <div>
            <h2 className="text-sm font-semibold">Kilde og datakontrol</h2>
            <p className="text-xs text-muted-foreground">
              {sources.length > 0
                ? 'Tallene på denne side er sammenstillet fra følgende kilder.'
                : 'Tallene er sammenstillet fra producent- og importøroplysninger samt redaktionel kontrol.'}
            </p>
          </div>

          {sources.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {sources.map((s) => (
                <li key={s.label}>
                  {s.url ? (
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-primary hover:border-primary/50 transition-colors"
                    >
                      {s.label}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="inline-flex items-center rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      {s.label}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="text-xs text-muted-foreground">
            {verifiedLabel
              ? `Senest kontrolleret: ${verifiedLabel}.`
              : 'Senest kontrolleret: ikke registreret endnu.'}{' '}
            Tjek altid endelige specifikationer og priser hos forhandleren.
          </p>
        </div>
      </div>
    </section>
  );
}
